import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import {
  getStockistLedger,
  listStockistReceivables,
  type StockistReceivable,
} from '../lib/api';

function money(v: string | null): string {
  const n = Number(v ?? 0);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Order-by-Area: per-stockist receivable balances (Main owes each stockist the
 * full retail sale value of orders it fulfilled). Read-only accrual view; cash
 * settlement is a future step.
 */
export function StockistReceivablesPage() {
  const [openBranch, setOpenBranch] = useState<StockistReceivable | null>(null);
  const listQ = useQuery({ queryKey: ['stockist-receivables'], queryFn: listStockistReceivables });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold">Stockist Receivables</h1>
        <p className="text-sm text-muted-foreground">
          What the company (Main) owes each stockist for house orders it fulfilled. Enable{' '}
          <span className="font-medium">Stockist receivables</span> in Settings for new orders to
          accrue here.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Balances</CardTitle>
        </CardHeader>
        <CardContent>
          {listQ.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (listQ.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No stockist branches yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b">
                    <th className="py-2 pr-4 font-medium">Stockist</th>
                    <th className="py-2 pr-4 font-medium text-right">Outstanding</th>
                    <th className="py-2 pr-4 font-medium text-right">Lifetime</th>
                    <th className="py-2 font-medium" />
                  </tr>
                </thead>
                <tbody>
                  {listQ.data!.map((s) => (
                    <tr key={s.branchId} className="border-b last:border-0">
                      <td className="py-2 pr-4">
                        <span className="font-medium">{s.branchName}</span>{' '}
                        <span className="text-muted-foreground font-mono text-xs">{s.branchCode}</span>
                      </td>
                      <td className="py-2 pr-4 text-right font-mono">₱{money(s.receivable)}</td>
                      <td className="py-2 pr-4 text-right font-mono text-muted-foreground">
                        ₱{money(s.lifetime)}
                      </td>
                      <td className="py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => setOpenBranch(s)}>
                          Ledger
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {openBranch && <LedgerCard branch={openBranch} onClose={() => setOpenBranch(null)} />}
    </div>
  );
}

function LedgerCard({ branch, onClose }: { branch: StockistReceivable; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['stockist-ledger', branch.branchId],
    queryFn: () => getStockistLedger(branch.branchId),
  });
  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">Ledger — {branch.branchName}</CardTitle>
        <Button size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (q.data?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">No entries yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground border-b">
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Type</th>
                  <th className="py-2 pr-4 font-medium">Order</th>
                  <th className="py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {q.data!.map((e) => (
                  <tr key={e.id} className="border-b last:border-0">
                    <td className="py-2 pr-4 whitespace-nowrap text-muted-foreground">
                      {new Date(e.createdAt).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4">
                      <Badge variant={e.direction === 'C' ? 'success' : 'secondary'}>
                        {e.kind === 'sale_receivable' ? 'Receivable' : 'Reversal'}
                      </Badge>
                    </td>
                    <td className="py-2 pr-4 font-mono text-xs">{e.orderCode ?? '—'}</td>
                    <td className="py-2 text-right font-mono">
                      {e.direction === 'C' ? '+' : '−'}₱{money(e.amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
